// Web Crypto API (AES-GCM) — chave NUNCA persistida.
// Mantida em memória de sessão. Salt fica no IndexedDB (meta).

import { useFiscalStore } from "@/store/fiscal-store";

let sessionKey: CryptoKey | null = null;

const enc = new TextEncoder();
const dec = new TextDecoder();

async function getSalt(): Promise<Uint8Array<ArrayBuffer>> {
  const row = await db.meta.get("salt");
  if (row?.value instanceof Uint8Array) {
    const src = row.value as Uint8Array;
    const copy = new Uint8Array(new ArrayBuffer(src.byteLength));
    copy.set(src);
    return copy as Uint8Array<ArrayBuffer>;
  }
  const salt = new Uint8Array(new ArrayBuffer(16));
  crypto.getRandomValues(salt);
  await db.meta.put({ key: "salt", value: salt });
  return salt as Uint8Array<ArrayBuffer>;
}

export async function deriveKeyFromPassword(password: string): Promise<CryptoKey> {
  const userId = useFiscalStore.getState().userId;
  const salt = await getSalt();
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  const key = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 150_000, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true, // Permite exportação para persistência no IndexedDB
    ["encrypt", "decrypt"],
  );
  
  sessionKey = key;

  // Persiste a chave vinculada ao usuário para desbloqueio automático
  if (userId) {
    await db.vault.put({ userId, key });
    console.log(`[crypto] Chave vinculada ao usuário ${userId} persistida no Vault Local.`);
  }

  return key;
}

export async function restoreKeyForUser(userId: string): Promise<boolean> {
  try {
    const entry = await db.vault.get(userId);
    if (entry?.key) {
      sessionKey = entry.key;
      console.log(`[crypto] Chave de desbloqueio automático restaurada para o usuário ${userId}.`);
      return true;
    }
  } catch (err) {
    console.error("[crypto] Erro ao restaurar chave do vault:", err);
  }
  return false;
}

export function hasSessionKey() {
  return sessionKey !== null;
}

export async function clearSessionKey() {
  sessionKey = null;
}

export async function encryptData(data: unknown): Promise<{ cipher: ArrayBuffer; iv: ArrayBuffer } | null> {
  if (!sessionKey) return null;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    sessionKey,
    enc.encode(JSON.stringify(data)),
  );
  return { cipher, iv: iv.buffer };
}

export async function decryptData<T = unknown>(cipher: ArrayBuffer, iv: ArrayBuffer): Promise<T | null> {
  if (!sessionKey) return null;
  try {
    const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, sessionKey, cipher);
    return JSON.parse(dec.decode(plain)) as T;
  } catch {
    return null;
  }
}

/** Versão "stateless" para o Worker: a chave é passada como parâmetro. */
export async function encryptWithKey(data: unknown, key: CryptoKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(JSON.stringify(data)),
  );
  return { cipher, iv: iv.buffer };
}
